import { type SetupContext } from "vue";
import type { EmitFn } from "vue/types/v3-setup-context";

export const useReactiveDialogSetup = (
  context: SetupContext<any> // Defining emits gives type SetupContext<("a"|"b"|"c")[]>, so allow for any.
) => {
  return useReactiveDialogScriptSetup(context.emit);
};

export const useReactiveDialogScriptSetup = <T = any>(
  emit: EmitFn | DialogEvents // Defining emits gives type EmitFn<("a"|"b"|"c")[]>, so allow for any.
) => {
  let close = () => emit("input", false);

  return {
    open: () => emit("input", true),
    close,
    accept: (payload?: T) => {
      emit("accept", payload);
      close();
    },
    cancel: () => {
      emit("cancel");
      close();
    },
  };
};

/**
 * To use imported types with `defineEmits()` and `defineProps()`, the
 * imported type must be an extension of another type which is defined
 * inside `<script setup>`.
 *
 * Using imported types directly is currently not supported.
 * See Vue documentation: https://vuejs.org/api/sfc-script-setup.html#typescript-only-features
 *
 * @example
 * interface EditSalesNoteDialogEvents<T extends number> extends DialogEvents<T> {}
 * const emit = defineEmits<EditSalesNoteDialogEvents<number>>();
 * // ok
 * emit("accept", 10);
 * // error
 * emit("accept", "test string")
 */
export interface DialogEvents<T = any> {
  (event: "input", value: boolean): void;
  (event: "accept", value: T): void;
  (event: "cancel"): void;
  (event: "close"): void;
}

export interface DialogProps {
  value: boolean;
}
